import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ColumnType, FilterTemplate } from '@app/shared/constants';
import { InboundFaxesTableModel, TableColumnConfig } from '@app/shared/models';
import { MfeCoreModule } from '@mynexus/mfe-core';
import { LibPipeModule } from '@mynexus/mynexus-ui-lib';
import { FilterService, LazyLoadEvent } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';

// Ideal vision is to have this as a generic table that can handle lazy loading. Keep business logic outside of this
// as much as possible. Influenced by the table component in this repo as well as CHAPP and DMEPOS Queue.
@Component({
    selector: 'ifa-table-lazy',
    templateUrl: './table-lazy.component.html',
    styleUrls: ['./table-lazy.component.scss'],
    imports: [
        CommonModule,
        TableModule,
        PaginatorModule,
        FormsModule,
        MultiSelectModule,
        LibPipeModule,
        ButtonModule,
        MfeCoreModule,
        TooltipModule,
        InputTextModule,
        InputIconModule,
        IconFieldModule
    ],
    providers: [FilterService]
})
export class TableLazyComponent {
  private readonly defaultRows = 10;

  @Input()
  public set columnConfig(columns: Array<TableColumnConfig>) {
    this.columns = columns.filter((c) => c.isVisible);
  }

  @Input() dataKey: string;
  @Input() filterable = false;
  @Input() stateKey: string;
  @Input() tableData: InboundFaxesTableModel[] = [];
  @Input() total: number;
  @Input() sortField: string;
  @Input() sortOrder: number;

  @Output() rowClicked = new EventEmitter<InboundFaxesTableModel>();
  @Output() lazyLoad = new EventEmitter<LazyLoadEvent>();

  public readonly FilterTemplate = FilterTemplate;
  public readonly ColumnType = ColumnType;
  public readonly selectedPreviewIndexList: Set<string> = new Set();
  public columns: Array<TableColumnConfig> = [];
  public rows: number = this.defaultRows;
  public selectionTriggered = false;

  @ViewChild('tableRef') private tableRef: Table;

  public selectionChange(target: InboundFaxesTableModel): void {
    this.rowClicked.emit(target);
  }

  public handlePage(pageState: PaginatorState): void {
    this.rows = pageState.rows;
  }

  public handleLazyLoad(event: LazyLoadEvent): void {
    this.lazyLoad.emit(event);
  }

  public getButtonIcon(col: TableColumnConfig): Record<string, boolean> {
    return {
      [col.buttonConfig.buttonIcon]: true,
    };
  }

  public getEmptyButtonIcon(col: TableColumnConfig): Record<string, boolean> {
    return {
      [col.buttonConfig.emptyIcon]: true,
    };
  }

  public filterText(field: string, value: string): void {
    const input = value.trim();
    if (value === '' || input.length > 2) {
      this.tableRef.filter(input, field, 'contains');
    }
  }

  public onPreviewClicked(targetColumn: TableColumnConfig, targetData: object): void {
    this.selectedPreviewIndexList.add(targetData[this.dataKey]);
    targetColumn.buttonConfig.onClicked(targetData);
  }

  public clearAllFilters(defaultSortField: string, defaultSortOrder: number): void {
    if (this.tableRef) {
      // Temporarily turning off lazy to prevent triggering onLazyLoad
      this.tableRef.lazy = false;
      this.tableRef.reset();

      // Some edge cases where the table won't reset the state automatically. Force it to update to match default values.
      this.tableRef.selection = null;
      this.tableRef.sortOrder = defaultSortOrder;
      this.tableRef.sortField = defaultSortField;
      this.tableRef.sortSingle();
      this.tableRef.saveState();

      // Update fields that sit outside of state after we manually save it
      this.tableRef.rows = this.defaultRows;
      this.tableRef.totalRecords = this.total;

      // Re-enable lazy loading
      this.tableRef.lazy = true;
    }
  }

  public clearSelection(): void {
    if (this.tableRef) {
      this.tableRef.selection = null;
      this.tableRef.saveState();
    }
  }
}
